import React from 'react';

import Avatar, { type SizeType } from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { type PersonProfileData } from '../../types';

const styles = cssMap({
	fill: {
		width: '100%',
	},
});

interface ProfileIdentityProps {
	data: PersonProfileData;
	avatarSize?: SizeType;
	showDepartment?: boolean;
	showLocation?: boolean;
	showPronouns?: boolean;
	showLocalTime?: boolean;
	actions?: React.ReactNode;
	variant?: 'full' | 'compact';
}

export function ProfileIdentity({
	data,
	avatarSize = 'xlarge',
	showDepartment = false,
	showLocation = true,
	showPronouns = false,
	showLocalTime = false,
	actions,
	variant = 'full',
}: ProfileIdentityProps) {
	const { avatarUrl, name, pronouns, jobTitle, department, location, localTime } = data;

	const subtitle = showDepartment && department ? `${jobTitle} \u2022 ${department}` : jobTitle;

	const locationLine =
		showLocalTime && localTime && location ? `${location} \u2013 ${localTime}` : location;

	if (variant === 'compact') {
		return (
			<Stack xcss={styles.fill} space="space.100">
				<Inline spread="space-between">
					<Avatar src={avatarUrl ?? undefined} name={name} size={avatarSize} />
					{actions}
				</Inline>
				<Stack space="space.025">
					<Inline space="space.100" alignBlock="end">
						<Heading as="h2" size="medium">
							{name}
						</Heading>
						{showPronouns && pronouns && (
							<Text size="small" color="color.text.subtlest">
								{pronouns}
							</Text>
						)}
					</Inline>
					<Stack space="space.075">
						{subtitle && (
							<Text maxLines={1} color="color.text.subtlest">
								{subtitle}
							</Text>
						)}
						{showLocation && locationLine && (
							<Text maxLines={1} size="small" color="color.text.subtlest">
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>
			</Stack>
		);
	}

	return (
		<Inline space="space.200" xcss={styles.fill} spread="space-between">
			<Inline space="space.200" alignBlock="center">
				<Avatar src={avatarUrl ?? undefined} name={name} size={avatarSize} />
				<Stack xcss={styles.fill} alignBlock="start">
					<Inline space="space.100" alignBlock="baseline">
						<Heading as="h2" size="medium">
							{name}
						</Heading>
						{showPronouns && pronouns && (
							<Text size="small" color="color.text.subtlest">
								{pronouns}
							</Text>
						)}
					</Inline>
					<Stack space="space.050">
						{subtitle && (
							<Text maxLines={1} color="color.text.subtlest">
								{subtitle}
							</Text>
						)}
						{showLocation && locationLine && (
							<Text maxLines={1} size="small" color="color.text.subtlest">
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>
			</Inline>
			{actions}
		</Inline>
	);
}
